// Espace employé — declare a worked week-end/férié day. Creates a
// RecupClaim (PENDING); a manager credits it from the /recup page.
import { useMemo, useState } from 'react';
import { useCreateMyClaim, useMeHolidays } from '../../api/hooks.js';
import { MiniMonth, cgD, cgFmtLong, cgToday, shiftYm } from '../conges/common.js';
import { toast } from '../../store/toast.js';

const offLabel = (d: string, holMap: Map<string, string>) => {
  const hol = holMap.get(d);
  if (hol) return `Jour férié · ${hol}`;
  const w = cgD(d).getUTCDay();
  return w === 5 ? 'Vendredi' : w === 6 ? 'Samedi' : 'Jour ouvrable';
};

export function EsClaimModal({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const [date, setDate] = useState<string | null>(null);
  const [demi, setDemi] = useState(false);
  const [motif, setMotif] = useState('');
  const [m0, setM0] = useState(cgToday().slice(0, 7));
  const [sent, setSent] = useState(false);
  const create = useCreateMyClaim();

  const m1 = shiftYm(m0, 1);
  const holidays = useMeHolidays(`${m0}-01`, `${m1}-31`);
  const holMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const h of holidays.data ?? []) map.set(h.date, h.name);
    return map;
  }, [holidays.data]);

  const send = () => {
    if (!date) return;
    create.mutate({ date, halfDay: demi, motif: motif.trim() || undefined }, {
      onSuccess: () => { setSent(true); setTimeout(onSent, 1400); },
      onError: (e) => {
        const body = (e as Error & { body?: { error?: string } }).body;
        toast.danger(body?.error === 'already_claimed'
          ? 'Cette journée a déjà été déclarée.'
          : body?.error === 'not_off_day'
            ? 'Seuls les week-ends et jours fériés ouvrent droit à récupération.'
            : "La déclaration n'a pas pu être envoyée.");
      },
    });
  };

  return (
    <div className="cg-veil center" onClick={onClose}>
      <div className="es-modal" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div className="es-sent">
            <span className="es-check">✓</span>
            <b>Déclaration envoyée</b>
            <span className="es-sent-sub">Après validation par votre responsable, {demi ? '0,5' : '1'} jour sera ajouté à votre stock de récupération.</span>
            <span className="es-sent-rec gm-mono">{date ? `${cgFmtLong(date)} · ${offLabel(date, holMap)}` : ''}</span>
          </div>
        ) : (<>
          <div className="es-mo-head">
            <div>
              <div className="sb-mo-title">Déclarer une journée travaillée</div>
              <div className="sb-mo-sub">Week-ends et jours fériés uniquement · 1 journée = 1 jour de récup.</div>
            </div>
            <button className="sb-dr-x" onClick={onClose}>✕</button>
          </div>
          <div className="cg-mo-calhead">
            <button className="gm-mn-arrow" onClick={() => setM0(shiftYm(m0, -1))}>‹</button>
            <span className="cg-mo-calrange">{date ? `${cgFmtLong(date)} · ${offLabel(date, holMap)}` : 'Sélectionnez le jour travaillé'}</span>
            <button className="gm-mn-arrow" onClick={() => setM0(shiftYm(m0, 1))}>›</button>
          </div>
          <div className="cg-mm-pair">
            <MiniMonth month={m0} sel={{ from: date, to: date }} onlyOff onPick={setDate} holidays={holMap} />
            <MiniMonth month={m1} sel={{ from: date, to: date }} onlyOff onPick={setDate} holidays={holMap} />
          </div>
          <div className="cg-hgrid two" style={{ marginTop: 14 }}>
            <button className={'cg-hbtn' + (!demi ? ' on' : '')} onClick={() => setDemi(false)}>Journée complète<i>+1,0 j de récup</i></button>
            <button className={'cg-hbtn' + (demi ? ' on' : '')} onClick={() => setDemi(true)}>Demi-journée<i>+0,5 j de récup</i></button>
          </div>
          <input className="cg-search wide" placeholder="Que faisiez-vous ? (inventaire, permanence, go-live…)" value={motif} onChange={(e) => setMotif(e.target.value)} />
          <div className="cg-mo-foot">
            <button className="cg-ghost" onClick={onClose}>Annuler</button>
            <button className="gm-attn-go" disabled={!date || create.isPending} onClick={send}>Envoyer la déclaration</button>
          </div>
        </>)}
      </div>
    </div>
  );
}
